import type { EventBus } from './eventBus';
import type { Worm } from '../entities/worm';

export type ToolType = 'drill' | 'blowtorch';

export interface ToolCallbacks {
  destroyTerrainCircle: (
    x: number,
    y: number,
    radius: number,
    type?: 'drill' | 'blowtorch',
  ) => void;
  playSound: (sound: 'drill' | 'blowtorch') => void;
  onTurnEnd: () => void;
}

export interface ToolDigResult {
  x: number;
  y: number;
  radius: number;
}

export class ToolSystemManager {
  private activeTool: ToolType | null = null;
  private toolTimeRemaining = 0;
  private toolUsedThisTurn = false;
  private toolFireHeld = false;
  private toolDigAccumulator = 0;

  private eventBus: EventBus | null = null;
  private callbacks: ToolCallbacks | null = null;

  private readonly DRILL_MAX_TIME = 3.0;
  private readonly BLOWTORCH_MAX_TIME = 5.0;
  private readonly TOOL_DIG_INTERVAL = 0.08;
  private readonly DRILL_DIG_DISTANCE = 3;
  private readonly BLOWTORCH_WALK_DISTANCE = 2;

  constructor(eventBus?: EventBus) {
    this.eventBus = eventBus ?? null;
  }

  setEventBus(eventBus: EventBus): void {
    this.eventBus = eventBus;
  }

  setCallbacks(callbacks: ToolCallbacks): void {
    this.callbacks = callbacks;
  }

  activate(tool: ToolType): void {
    this.activeTool = tool;
    this.toolTimeRemaining = tool === 'drill' ? this.DRILL_MAX_TIME : this.BLOWTORCH_MAX_TIME;
    this.toolUsedThisTurn = true;
    this.toolFireHeld = true;
    this.toolDigAccumulator = 0;

    this.callbacks?.playSound(tool);
    this.eventBus?.emit('tool:activated', { tool });
  }

  deactivate(endTurn = false): void {
    if (this.activeTool) {
      this.eventBus?.emit('tool:deactivated', { tool: this.activeTool, endTurn });
    }
    this.activeTool = null;
    this.toolFireHeld = false;
    this.toolDigAccumulator = 0;

    if (endTurn) {
      this.callbacks?.onTurnEnd();
    }
  }

  update(deltaTime: number, worm: Worm): ToolDigResult | null {
    if (!this.activeTool || this.toolTimeRemaining <= 0) return null;

    if (!this.toolFireHeld) return null;

    this.toolTimeRemaining -= deltaTime;
    this.toolDigAccumulator += deltaTime;

    let result: ToolDigResult | null = null;

    while (this.toolDigAccumulator >= this.TOOL_DIG_INTERVAL) {
      this.toolDigAccumulator -= this.TOOL_DIG_INTERVAL;

      const bounds = worm.getBounds();
      const centerX = worm.position.x + bounds.width / 2;

      if (this.activeTool === 'drill') {
        const digY = worm.position.y + bounds.height + 5;
        result = { x: centerX, y: digY, radius: 14 };
        this.callbacks?.destroyTerrainCircle(centerX, digY, 14, 'drill');
        worm.position.y += this.DRILL_DIG_DISTANCE;
        worm.isGrounded = false;
      } else if (this.activeTool === 'blowtorch') {
        const rad = (worm.aimAngle * Math.PI) / 180;
        const aimRad = worm.facing === 'right' ? rad : Math.PI - rad;
        const digX = centerX + Math.cos(aimRad) * 18;
        const digY = worm.position.y + bounds.height / 2 - Math.sin(aimRad) * 18;
        result = { x: digX, y: digY, radius: 14 };
        this.callbacks?.destroyTerrainCircle(digX, digY, 14, 'blowtorch');

        const walkDir = worm.facing === 'right' ? 1 : -1;
        worm.position.x += walkDir * this.BLOWTORCH_WALK_DISTANCE;
      }
    }

    if (this.toolTimeRemaining <= 0) {
      this.deactivate(true);
    }

    return result;
  }

  setFireHeld(held: boolean): void {
    this.toolFireHeld = held;
  }

  reset(): void {
    this.activeTool = null;
    this.toolUsedThisTurn = false;
    this.toolTimeRemaining = 0;
    this.toolFireHeld = false;
    this.toolDigAccumulator = 0;
  }

  isActive(): boolean {
    return this.activeTool !== null;
  }

  getActiveTool(): ToolType | null {
    return this.activeTool;
  }

  getTimeRemaining(): number {
    return this.toolTimeRemaining;
  }

  wasUsedThisTurn(): boolean {
    return this.toolUsedThisTurn;
  }

  isFireHeld(): boolean {
    return this.toolFireHeld;
  }

  canSwitchWeapon(weaponId: string): boolean {
    if (!this.toolUsedThisTurn) return true;
    return weaponId === 'drill' || weaponId === 'blowtorch';
  }

  getDrillMaxTime(): number {
    return this.DRILL_MAX_TIME;
  }

  getBlowtorchMaxTime(): number {
    return this.BLOWTORCH_MAX_TIME;
  }
}
